<template>
    <div class="book-preview" :id="id" ref="preview-root">
        <div v-if="loading" class="book-loading" ref="book-loading">
            <div class="loading-bar-wrapper">
                <div class="loading-number-wrapper">
                    <h4 class="loading-number">{{ loadingPercentage }}%</h4>
                </div>
                <div class="loading-bar">
                    <div class="loading-bar-track">
                        <div
                            class="loading-bar-fill"
                            :style="{ width: loadingPercentage + '%' }"
                        >
                            <span class="loading-number-mobile"
                                >{{ loadingPercentage }}%</span
                            >
                        </div>
                        <div class="loading-bar-glow" :style="{ width: loadingPercentage + '%' }"></div>
                    </div>
                </div>
                <p v-show="loadingPercentage >= 99" class="loading-stuck-text">{{ loadingStuckLabel }}</p>
            </div>
            <img
                v-if="loadingBackgroundAssetUrl !== ''"
                :src="loadingBackgroundAssetUrl"
                class="loading-book-img"
                width="291"
                alt="Dreamy Tales Buch"
            />
            <Swiper
                :modules="[Autoplay]"
                :loop="false"
                :rewind="true"
                :slides-per-view="loadingSliderSlidesPerView"
                :space-between="24"
                :navigation="false"
                :centeredSlides="true"
                :autoplay="{
                    delay: loadingSliderSlideDuration * 1000,
                    disableOnInteraction: false,
                }"
                @swiper="onLoadingSwiper"
                @slideChange="onLoadingSlideChange"
            >
                <SwiperSlide
                    v-for="(slide, index) in loadingSliderContent"
                    :key="index"
                    :style="getLoadingSlideStyle(index)"
                >
                    <img
                        :src="slide.loadingSlideAssetUrl"
                        alt="Slide Image"
                        style="max-width: 100%"
                    />
                </SwiperSlide>
            </Swiper>
        </div>

        <div v-if="!loading && loadError" class="book-error">
            <p>{{ loadError }}</p>
        </div>

        <!-- Walnut Mode (A5 Vertical) -->
        <div
            v-if="book && book.pages && !loading && previewMode === 'walnut'"
            :class="['book-content', 'walnut-mode', { 'is-changing': changing }]"
        >
            <div class="swiper-wrapper">
                <button
                    :class="[
                        'custom-swiper-prev',
                        { disabled: previewSliderActiveIndex === 0 },
                    ]"
                    @click="handlePrevClick"
                ></button>
                <Swiper
                    :loop="false"
                    :slides-per-view="1"
                    :space-between="16"
                    :navigation="false"
                    @swiper="onPreviewSwiper"
                    @slideChange="onPreviewSlideChange"
                >
                    <SwiperSlide
                        v-for="(page, key, index) in book.pages"
                        :key="index"
                    >
                        <div v-if="index === 0" class="first-page">
                            <div class="page-title">
                                <span v-if="showTitlePrefix" class="title-prefix">{{
                                    titlePrefix
                                }}</span
                                ><br v-if="showTitlePrefix" />
                                <span
                                    class="title"
                                >{{ processedTitleContent }}</span>
                            </div>
                            <div class="page-images">
                                <img
                                    v-if="page.img"
                                    @error="onPreviewImageError"
                                    class="page-image"
                                    :src="page.img"
                                    width="400"
                                    alt="Preview Image - Page 1"
                                />
                                <img
                                    src="https://cdn.shopify.com/s/files/1/0753/6023/9952/files/Title.png?v=1746520032"
                                    class="page-overlay"
                                    width="400"
                                    alt="First Page Overlay"
                                />
                            </div>
                        </div>
                        <div v-else class="page-n">
                            <div class="page-images">
                                <img
                                    v-if="page.img"
                                    @error="onPreviewImageError"
                                    class="page-image"
                                    :src="page.img"
                                    width="400"
                                    alt="Preview Image - Page n"
                                />
                                <img
                                    src="@/assets/customizer_preview_DT_standard-page-min.png"
                                    class="page-overlay"
                                    width="400"
                                    alt="Page Overlay"
                                />
                            </div>
                            <div class="page-text" ref="page-text-container">
                                <p ref="page-text-paragraph">{{ page.text }}</p>
                            </div>
                        </div>
                    </SwiperSlide>
                </Swiper>
                <button
                    :class="[
                        'custom-swiper-next',
                        {
                            disabled:
                                book.pages &&
                                previewSliderActiveIndex ===
                                    Object.keys(book.pages).length - 1,
                        },
                    ]"
                    @click="handleNextClick"
                ></button>
            </div>
            <div class="swiper-pagination">
                <span v-for="(page, key, index) in book.pages" :key="index">
                    <span
                        :class="{ active: index === previewSliderActiveIndex }"
                        @click="handlePaginationClick(index)"
                    ></span>
                </span>
            </div>
        </div>

        <!-- Softcover Mode (Square) -->
        <div
            v-if="book && book.pages && !loading && previewMode === 'softcover'"
            :class="['book-content', 'softcover-mode', { 'is-changing': changing }]"
        >
            <div class="swiper-wrapper">
                <button
                    :class="[
                        'custom-swiper-prev',
                        { disabled: previewSliderActiveIndex === 0 },
                    ]"
                    @click="handlePrevClick"
                ></button>
                <Swiper
                    :loop="false"
                    :slides-per-view="1"
                    :space-between="16"
                    :navigation="false"
                    @swiper="onPreviewSwiper"
                    @slideChange="onPreviewSlideChange"
                >
                    <SwiperSlide
                        v-for="(slide, index) in softcoverSlides"
                        :key="index"
                    >
                        <!-- Title Page -->
                        <div v-if="slide && slide.type === 'title'" class="softcover-title-page">
                            <div class="page-title">
                                <span v-if="showTitlePrefix" class="title-prefix">{{
                                    titlePrefix
                                }}</span
                                ><br v-if="showTitlePrefix" />
                                <span
                                    class="title"
                                >{{ processedTitleContent }}</span>
                            </div>
                            <div class="softcover-title-images">
                                <img
                                    v-if="slide.img"
                                    @error="onPreviewImageError"
                                    class="softcover-title-background"
                                    :src="slide.img"
                                    width="400"
                                    alt="Softcover Title Background"
                                />
                                <img
                                    src="https://cdn.shopify.com/s/files/1/0753/6023/9952/files/title_SC.png?v=1759749909"
                                    class="softcover-title-overlay"
                                    width="400"
                                    alt="Softcover Title Page"
                                />
                            </div>
                        </div>
                        <!-- Image Page -->
                        <div v-else-if="slide && slide.type === 'image'" class="softcover-image-page">
                            <img
                                v-if="slide.img"
                                @error="onPreviewImageError"
                                class="softcover-page-image"
                                :src="slide.img"
                                width="400"
                                alt="Softcover Preview Image"
                            />
                        </div>
                        <!-- Text Page -->
                        <div v-else-if="slide && slide.type === 'text'" class="softcover-text-page">
                            <img
                                src="@/assets/customizer_seemless_paper_texture_2.png"
                                class="softcover-paper-texture"
                                width="400"
                                alt="Paper Texture"
                            />
                            <div class="softcover-text-content">
                                <p>{{ slide.text }}</p>
                            </div>
                        </div>
                    </SwiperSlide>
                </Swiper>
                <button
                    :class="[
                        'custom-swiper-next',
                        {
                            disabled:
                                softcoverSlides &&
                                previewSliderActiveIndex ===
                                    softcoverSlides.length - 1,
                        },
                    ]"
                    @click="handleNextClick"
                ></button>
            </div>
            <div class="swiper-pagination">
                <span v-for="(slide, index) in softcoverSlides" :key="index">
                    <span
                        :class="{ active: index === previewSliderActiveIndex }"
                        @click="handlePaginationClick(index)"
                    ></span>
                </span>
            </div>
        </div>

        <div v-if="changing" class="change-indicator">
            <LoadingIndicator :size="24" />
        </div>
    </div>
</template>

<script>
import {
    ref,
    onMounted,
    onBeforeUnmount,
    inject,
    useTemplateRef,
    nextTick,
    computed,
    watch,
    getCurrentInstance,
} from "vue";
import { useRoute } from "vue-router";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import axios from "axios";
import { isEditing, isPreviewing } from "@builder.io/sdk-vue";
import EventBus from "../event-bus";
import LoadingIndicator from "./LoadingIndicator.vue";
import { trackEvent } from "../utils/tracking";
import confetti from 'canvas-confetti';
import DraftService, {
    redirectToStep1,
    isDraftDataValid,
    storeDraftMeta,
    clearDraftMeta,
    getDraftIdFromUrlCached,
    getDraftPayloadFromResponse,
} from "../utils/draftService";
import {
    captureCustomizerException,
    captureCustomizerMessage,
    incrementCustomizerMetric,
} from "../utils/sentry";
import { createPreviewImageErrorReporter } from "../utils/previewImageReporting";
import { safeGetJson } from "../utils/safeStorage";
import { getTokenValue, resolveTextTokens } from "../utils/textTokens";
import {
    createPreviewCancellation,
    createPreviewPollStorage,
    extractPreviewPollJobId,
    extractPreviewPollResult,
    getPreviewHttpJsonConfig,
    getPreviewPollStatus,
    hasPreviewStoredValues,
    isPreviewPollCompleted,
    isPreviewPollFailed,
    isPreviewPollRunning,
    normalizePreviewPollInterval,
    restorePreviewDraftValues,
    runPreviewRequest,
} from "../utils/previewService";
import {
    clearPreviewPollStart,
    runPreviewPollStartOnce,
} from "../utils/previewPollStartGuard";
import { startWhenBuilderVariantActive } from "../utils/builderVariantActivity";
import {
    attachPollErrorDiagnostics,
    getAxiosCode,
    getAxiosHttpStatus,
    getPollElapsedMs,
    getPollStatusRequestDescriptor as createPollStatusRequestDescriptor,
    getPreviewFailureMetricAttributes,
    getPreviewFailureReason,
    getPreviewRequestDiagnostics,
    runPreviewStatusPolling,
} from "../utils/previewPollingResilience";
import { builderText, customizerText } from "../utils/customizerTexts";

export default {
    name: "BookPreview",
    components: {
        Swiper,
        SwiperSlide,
        LoadingIndicator,
    },
    props: {
        id: {
            type: String,
            default: "",
        },
        requestUrl: {
            type: String,
            required: true,
        },
        requestMode: {
            type: String,
            default: "fetch",
            validator: (value) => ["fetch", "poll"].includes(value),
        },
        pollStartUrl: {
            type: String,
            default: "",
        },
        pollStatusUrl: {
            type: String,
            default: "",
        },
        pollIntervalMs: {
            type: Number,
            default: 3000,
            validator: (value) => value >= 500,
        },
        pollTimeoutMs: {
            type: Number,
            default: 360000,
            validator: (value) => value >= 0,
        },
        minLoadingDuration: {
            type: Number,
            default: 10,
            validator: (value) => value >= 0,
        },
        loadingBackgroundAssetUrl: {
            type: String,
            default: "",
        },
        loadingSliderContent: {
            type: Array,
            default: () => [],
        },
        loadingSliderSlideDuration: {
            type: Number,
            default: 3,
            validator: (value) => value >= 0,
        },
        titlePrefix: {
            type: String,
            default: "",
        },
        titleContent: {
            type: String,
            default: "",
        },
        fadeInClass: {
            type: String,
            default: "",
        },
        hideAfterLoadingClass: {
            type: String,
            default: "",
        },
        loadingStuckText: {
            type: String,
            default: "",
        },
        previewMode: {
            type: String,
            default: "walnut",
            validator: (value) => ["walnut", "softcover"].includes(value),
        },
    },
    setup(props) {
        const onPreviewImageError = createPreviewImageErrorReporter({
            component: "BookPreview",
            getProductType: () => "book",
        });
        const pageSettings = inject("pageSettings");
        const route = useRoute();
        
        const loading = ref(true);
        const previewRoot = useTemplateRef("preview-root");
        const bookLoading = useTemplateRef("book-loading");
        const loadingPercentage = ref(0);
        const loadingSwiper = ref(null);
        const loadingSliderActiveIndex = ref(0);
        const loadingSliderSlidesPerView = ref(1.5);
        const showLoadingStuckText = computed(() => loadingPercentage.value >= 99 && loading.value);
        const previewSwiper = ref(null);
        const previewSliderActiveIndex = ref(0);
        const pageTextContainer = useTemplateRef("page-text-container");
        const pageTextParagraph = useTemplateRef("page-text-paragraph");
        const book = ref(null);
        const changing = ref(false);
        const instance = getCurrentInstance();
        const hasInteractedWithPreview = ref(false);
        const draftData = ref(null);
        const loadError = ref("");
        const isDevMode = process.env.VUE_APP_DEV_MODE === "true";
        const POLL_JOB_STORAGE_PREFIX = "dreamytales:book-preview-poll";
        const POLL_CANCELLED_ERROR = "BOOK_PREVIEW_POLL_CANCELLED";
        let componentActive = true;
        let previewOutcomeReported = false;
        let previewActivated = false;
        let stopBuilderVariantActivity = null;
        
        const softcoverSlides = computed(() => {
            if (!book.value?.pages) return [];
            
            const slides = [];
            const pages = Object.values(book.value.pages);
            
            slides.push({
                type: 'title',
                img: pages[0]?.img,
                text: pages[0]?.text
            });
            
            for (let i = 1; i < pages.length; i++) {
                const page = pages[i];
                
                if (page.img) {
                    slides.push({
                        type: 'image',
                        img: page.img,
                        text: page.text
                    });
                }
                
                if (page.text) {
                    slides.push({
                        type: 'text',
                        img: page.img,
                        text: page.text
                    });
                }
            }
            
            return slides;
        });

        function onLoadingSwiper(_swiper) {
            loadingSwiper.value = _swiper;
        }

        function setLoadingSliderSlidesPerView() {
            if (window.innerWidth <= 640) {
                loadingSliderSlidesPerView.value = 1;
            } else {
                loadingSliderSlidesPerView.value = 1.5;
            }
        }
        setLoadingSliderSlidesPerView();
        window.addEventListener("resize", setLoadingSliderSlidesPerView);

        function onLoadingSlideChange() {
            if (!loadingSwiper.value) {
                return;
            }

            loadingSliderActiveIndex.value = loadingSwiper.value.realIndex;
        }

        function buildBookPayload(pages, payload) {
            const nextBook = { pages };

            if (Object.prototype.hasOwnProperty.call(payload || {}, "changeCounter")) {
                nextBook.changeCounter = payload.changeCounter;
            }

            return nextBook;
        }

        function getLoadingSlideStyle(index) {
            return {
                opacity: index === loadingSliderActiveIndex.value ? 1 : 0.4,
            };
        }
        
        function showConfetti() {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
                colors: [
                    '#7EBBCC', // $color-primary-dt-blue
                    '#F99B70', // $color-secondary-dt-orange
                    '#D8ECF2', // $color-dt-light-blue-1
                    '#57a5bb', // $color-dt-dark-blue-1
                    '#4D92A5', // $color-dt-dark-blue-2
                    '#FCD5C3', // $color-dt-light-orange-1
                    '#D99272', // $color-dt-dark-orange-1
                    '#B37457', // $color-dt-dark-orange-2
                    '#2B2B2B'  // $color-new-black
                ]
            });
        }
        
        function trackPreviewInteraction() {
            if (hasInteractedWithPreview.value) {
                return;
            }
            
            hasInteractedWithPreview.value = true;
            
            if (!instance?.proxy?.$gtm) {
                return;
            }
            
            trackEvent(instance.proxy, {
                event: "interacted_with_preview", 
                category: "Customizer",
                action: "PreviewSwipe",
                label: `Page ${previewSliderActiveIndex.value + 1}`,
                page_number: previewSliderActiveIndex.value + 1
            });
            
        }
        
        function handlePrevClick() {
            if (previewSwiper.value && previewSliderActiveIndex.value > 0) {
                trackPreviewInteraction();
                previewSwiper.value.slidePrev();
            }
        }
        
        function handleNextClick() {
            const maxSlides = props.previewMode === 'softcover' 
                ? softcoverSlides.value.length 
                : Object.keys(book.value?.pages || {}).length;
                
            if (previewSwiper.value && previewSliderActiveIndex.value < maxSlides - 1) {
                trackPreviewInteraction();
                previewSwiper.value.slideNext();
            }
        }
        
        function handlePaginationClick(index) {
            if (previewSwiper.value && index !== previewSliderActiveIndex.value) {
                trackPreviewInteraction();
                previewSwiper.value.slideTo(index);
            }
        }

        function saveDraftValuesToStorage(loadedDraftData) {
            restorePreviewDraftValues(loadedDraftData, pageSettings);
        }
        
        const hasStoredValues = hasPreviewStoredValues;
        
        const getFailureSentryTags = (attributes, diagnostics = {}) => {
            const safeDiagnostics = getPreviewFailureMetricAttributes(diagnostics);

            return {
                ...attributes,
                ...safeDiagnostics,
                host: window.location.host,
                poll_phase: diagnostics.pollPhase || "none",
                http_status: safeDiagnostics.response_status,
                axios_code: diagnostics.axiosCode || "none",
                elapsed_ms: diagnostics.elapsedMs ?? "unknown",
            };
        };

        const getFailureSentryContext = (diagnostics = {}) => ({
            failed_host: getPreviewFailureMetricAttributes(diagnostics).failed_host,
            finalStatusCheck: Boolean(diagnostics.finalCheck),
            variant: diagnostics.variant || route.params.bookId || null,
        });

        const reportPreviewOutcome = (outcome, failureReason = "none", diagnostics = {}) => {
            if (previewOutcomeReported) return;

            previewOutcomeReported = true;
            const attributes = {
                component: "BookPreview",
                product_type: "book",
                request_mode: props.requestMode === "poll" ? "poll" : "fetch",
                outcome,
                failure_reason: failureReason,
            };

            incrementCustomizerMetric(
                "customizer.preview_load",
                outcome === "failure"
                    ? { ...attributes, ...getPreviewFailureMetricAttributes(diagnostics) }
                    : attributes
            );

            // A missing draft is counted above, not reported: it comes from bad links, not our code.
            if (outcome === "failure" && failureReason !== "draft_not_found") {
                captureCustomizerMessage("customizer_preview_load_failed", {
                    level: "error",
                    tags: getFailureSentryTags(attributes, diagnostics),
                    context: getFailureSentryContext(diagnostics),
                });
            }
        };

        const showLoadError = (message, failureReason = "unknown", diagnostics = {}) => {
            reportPreviewOutcome("failure", failureReason, diagnostics);
            loadError.value = message;
            loading.value = false;
            if (bookLoading.value) {
                bookLoading.value.style.opacity = 0;
            }
        };

        const handleDraftLoadFailure = (
            reason,
            draftLoadTriggered = false,
            loadedDraftData = null,
            diagnostics = {}
        ) => {
            if (draftLoadTriggered) {
                if (loadedDraftData) {
                    saveDraftValuesToStorage(loadedDraftData);
                }
                
                const hasStepData = hasStoredValues(pageSettings.step1StorageKey) && hasStoredValues(pageSettings.step2StorageKey);
                
                if (hasStepData) {
                    loadBookNormal({ allowRedirect: false });
                } else {
                    showLoadError(
                        customizerText("draftLoadError"),
                        diagnostics.httpStatus === 404 ? "draft_not_found" : "draft_load_failed",
                        diagnostics
                    );
                }
                return;
            }
            
            redirectToStep1(reason, pageSettings);
        };

        const shouldStayOnCurrentStepForPreview = () => {
            return isDevMode || isEditing() || isPreviewing();
        };

        const getNormalizedRequestMode = () => props.requestMode === "poll" ? "poll" : "fetch";

        const getHttpJsonConfig = getPreviewHttpJsonConfig;

        const applyBookResponseData = (responseData) => {
            const payload = getDraftPayloadFromResponse(responseData);

            book.value = buildBookPayload(payload?.pages, payload);
            EventBus.emit("bookLoaded", book.value);

            const responseDraftId = payload?.draftid || null;
            if (responseDraftId) {
                storeDraftMeta(route.params.bookId || null, responseDraftId);

            }

            return payload;
        };

        const finishBookLoading = () => {
            if (!componentActive) return;

            reportPreviewOutcome("success");

            if (bookLoading.value) {
                bookLoading.value.style.opacity = 0;
            }

            if (props.fadeInClass !== "") {
                const contentBoxes = document.getElementsByClassName(
                    props.fadeInClass
                );
                for (let i = 0; i < contentBoxes.length; i++) {
                    contentBoxes[i].style.display = "flex";
                    contentBoxes[i].style.transition = "opacity 0.3s";
                    contentBoxes[i].style.opacity = 1;
                }
            }

            if (props.hideAfterLoadingClass !== "") {
                const elementsToHide = document.getElementsByClassName(
                    props.hideAfterLoadingClass
                );
                for (let i = 0; i < elementsToHide.length; i++) {
                    elementsToHide[i].style.transition = "opacity 0.3s";
                    elementsToHide[i].style.opacity = 0;
                    setTimeout(() => {
                        elementsToHide[i].style.display = "none";
                    }, 300);
                }
            }

            setTimeout(() => {
                if (!componentActive) return;
                loading.value = false;
                EventBus.emit("stepThreeShowContent");

                showConfetti();
            }, 300);
        };

        const pollCancellation = createPreviewCancellation({
            cancelCode: POLL_CANCELLED_ERROR,
            isActive: () => componentActive,
        });
        const pollStorage = createPreviewPollStorage({
            prefix: POLL_JOB_STORAGE_PREFIX,
            getBookId: () => route.params.bookId,
            getPollStartUrl: () => props.pollStartUrl,
            getPollStatusUrl: () => props.pollStatusUrl,
            getPollTimeoutMs: () => props.pollTimeoutMs,
        });
        const getPollJobStorageKey = pollStorage.getStorageKey;
        const normalizePollIntervalMs = (value, fallback = props.pollIntervalMs) => (
            normalizePreviewPollInterval(value, fallback)
        );
        const clearPollTimeoutHandle = pollCancellation.clear;
        const isPollCancelledError = pollCancellation.isCancelled;
        const assertPollActive = pollCancellation.assertActive;
        const waitForPollInterval = pollCancellation.wait;
        const isPollJobExpired = pollStorage.isExpired;
        const getStoredPollJob = pollStorage.get;
        const persistPollJob = pollStorage.persist;
        const clearStoredPollJob = pollStorage.clear;
        const clearStoredPollJobForRequest = pollStorage.clearForRequest;

        const getPollStatusValue = getPreviewPollStatus;
        const isPollCompleted = isPreviewPollCompleted;
        const isPollFailed = isPreviewPollFailed;
        const isPollRunning = isPreviewPollRunning;
        const extractPollJobId = extractPreviewPollJobId;
        const extractPollResult = (payload) => extractPreviewPollResult(
            payload,
            (candidate) => Boolean(candidate.pages)
        );

        const buildPollDiagnostics = ({
            error = null,
            pollJob = null,
            descriptor = null,
            response = null,
            pollPhase = "status",
            failureReason = "request_failed",
            finalCheck = false,
        } = {}) => ({
            component: "BookPreview",
            productType: "book",
            requestMode: getNormalizedRequestMode(),
            jobId: pollJob?.jobId || null,
            pollPhase,
            requestUrl: descriptor?.url || (pollPhase === "start" ? props.pollStartUrl : props.pollStatusUrl),
            httpStatus: response?.status || getAxiosHttpStatus(error),
            axiosCode: getAxiosCode(error),
            elapsedMs: getPollElapsedMs(pollJob),
            host: window.location.host,
            variant: pollJob?.variantID || route.params.bookId || null,
            failureReason,
            finalCheck,
        });

        const createPollError = (message, failureReason, diagnostics = {}) => (
            attachPollErrorDiagnostics(new Error(message), {
                ...diagnostics,
                failureReason,
            })
        );

        const ensurePollConfig = () => {
            if (!props.pollStartUrl || !props.pollStatusUrl) {
                throw new Error("Polling ist nicht vollständig konfiguriert.");
            }
        };

        const getPollStatusRequestDescriptor = (jobId) => (
            createPollStatusRequestDescriptor(props.pollStatusUrl, jobId)
        );

        const getPollStatusRequest = (descriptor) => {
            if (descriptor.method === "get") {
                // sentry-ignore: status checks are retried; the final outcome is reported via reportPreviewOutcome
                return axios.get(descriptor.url, { ...getHttpJsonConfig(30000), sentryReport: false });
            }

            // sentry-ignore: status checks are retried; the final outcome is reported via reportPreviewOutcome
            return axios.post(descriptor.url, descriptor.body, { ...getHttpJsonConfig(30000), sentryReport: false });
        };

        const evaluatePollPayload = (payload, storageKey, requestBody, fallbackIntervalMs, requireKnownStatus = true, diagnostics = {}) => {
            const status = getPollStatusValue(payload);

            if (isPollCompleted(payload)) {
                const result = extractPollResult(payload);
                if (!result) {
                    throw createPollError(
                        "Polling wurde abgeschlossen, aber es fehlt result.",
                        "missing_result",
                        diagnostics
                    );
                }

                applyBookResponseData(result, "Poll book response received");
                clearStoredPollJob(storageKey);
                return { done: true, pollJob: null, intervalMs: fallbackIntervalMs };
            }

            if (isPollFailed(payload)) {
                clearStoredPollJob(storageKey);
                throw createPollError(
                    payload?.message || payload?.error || "Book preview generation failed",
                    "backend_failed",
                    diagnostics
                );
            }

            if (requireKnownStatus && (!status || !isPollRunning(payload))) {
                throw createPollError(
                    `Unbekannter Polling-Status: ${status || "leer"}`,
                    "unknown_status",
                    diagnostics
                );
            }

            const existingPollJob = safeGetJson(storageKey, {});
            const intervalMs = normalizePollIntervalMs(payload?.pollIntervalMs, fallbackIntervalMs);
            const pollJob = persistPollJob(storageKey, requestBody, {
                jobId: extractPollJobId(payload) || existingPollJob.jobId,
                status: status || "processing",
                pollIntervalMs: intervalMs,
                expiresAt: payload?.expiresAt || existingPollJob.expiresAt || null,
            });

            return { done: false, pollJob, intervalMs };
        };

        const pollPreviewJob = async (initialPollJob, requestBody, initialIntervalMs = props.pollIntervalMs) => {
            return runPreviewStatusPolling({
                initialPollJob,
                requestBody,
                initialIntervalMs,
                pollTimeoutMs: props.pollTimeoutMs,
                waitForPollInterval,
                assertPollActive,
                isPollJobExpired,
                clearStoredPollJob,
                persistPollJob,
                normalizePollIntervalMs,
                getStatusRequestDescriptor: getPollStatusRequestDescriptor,
                requestStatus: getPollStatusRequest,
                getStatusPayload: (response) => getDraftPayloadFromResponse(response.data),
                evaluateStatusPayload: evaluatePollPayload,
                buildDiagnostics: buildPollDiagnostics,
                isCancelledError: isPollCancelledError,
            });
        };

        const startPollingPreview = async (requestBody) => {
            ensurePollConfig();
            const storageKey = getPollJobStorageKey(requestBody);
            const storedPollJob = getStoredPollJob(requestBody);

            if (storedPollJob) {
                return pollPreviewJob(storedPollJob, requestBody, storedPollJob.pollIntervalMs);
            }

            const startPollJob = {
                jobId: null,
                createdAt: Date.now(),
                variantID: requestBody.variantID,
            };
            const startDescriptor = {
                method: "post",
                url: props.pollStartUrl,
                body: null,
            };
            let startResponse;

            try {
                startResponse = await runPreviewPollStartOnce(
                    storageKey,
                    ({ idempotencyKey }) => axios.post(
                        props.pollStartUrl,
                        { ...requestBody, idempotencyKey },
                        // sentry-ignore: the start is retried on network errors; the final outcome is reported via reportPreviewOutcome
                        { ...getHttpJsonConfig(30000), sentryReport: false }
                    ),
                    { isActive: () => componentActive }
                );
            } catch (error) {
                throw attachPollErrorDiagnostics(error, buildPollDiagnostics({
                    error,
                    pollJob: startPollJob,
                    descriptor: startDescriptor,
                    pollPhase: "start",
                    failureReason: "request_failed",
                }));
            }

            const startPayload = getDraftPayloadFromResponse(startResponse.data);
            const jobId = extractPollJobId(startPayload);
            const startDiagnostics = buildPollDiagnostics({
                pollJob: {
                    ...startPollJob,
                    jobId,
                },
                descriptor: startDescriptor,
                response: startResponse,
                pollPhase: "start",
            });

            if (!jobId && isPollFailed(startPayload)) {
                throw createPollError(
                    startPayload?.message || startPayload?.error || "Book preview generation failed",
                    "backend_failed",
                    startDiagnostics
                );
            }

            if (!jobId && isPollCompleted(startPayload)) {
                const result = extractPollResult(startPayload);
                if (!result) {
                    throw createPollError(
                        "Polling wurde abgeschlossen, aber es fehlt result.",
                        "missing_result",
                        startDiagnostics
                    );
                }

                applyBookResponseData(result, "Poll book response received");
                return;
            }

            if (!jobId) {
                throw createPollError(
                    "Polling konnte nicht gestartet werden: jobId fehlt.",
                    "missing_job_id",
                    startDiagnostics
                );
            }

            const startedPollJob = persistPollJob(storageKey, requestBody, {
                jobId,
                status: getPollStatusValue(startPayload) || "queued",
                pollIntervalMs: normalizePollIntervalMs(startPayload?.pollIntervalMs, props.pollIntervalMs),
                expiresAt: startPayload?.expiresAt || null,
                createdAt: Date.now(),
            });

            const startEvaluation = evaluatePollPayload(
                { ...(startPayload || {}), jobId },
                storageKey,
                requestBody,
                startedPollJob.pollIntervalMs,
                false,
                startDiagnostics
            );

            if (startEvaluation.done) {
                return;
            }

            return pollPreviewJob(
                { ...startEvaluation.pollJob, jobId },
                requestBody,
                startEvaluation.intervalMs
            );
        };

        const makeBookRequest = (requestBody, options = {}) => {
            const allowRedirect = options.allowRedirect !== false;
            const requestMode = getNormalizedRequestMode();
            let fetchBookSuccess = true;
            let previewRequestError = null;
            let previewRequestDiagnostics = {};

            if (requestMode === "poll" && options.forceNewPollJob === true) {
                clearStoredPollJobForRequest(requestBody);
                clearPreviewPollStart(getPollJobStorageKey(requestBody));
            }

            const requestStartedAt = Date.now();
            const fetchBookPromise = runPreviewRequest({
                requestMode,
                requestBody,
                minLoadingDuration: props.minLoadingDuration,
                setLoadingPercentage: (value) => { loadingPercentage.value = value; },
                startPolling: startPollingPreview,
                fetchPreview: (body) => axios.post(
                    props.requestUrl,
                    body,
                    // sentry-ignore: the preview outcome is reported via reportPreviewOutcome
                    { ...getHttpJsonConfig(360000), sentryReport: false }
                ),
                applyFetchResult: (response) => {
                    applyBookResponseData(response.data, "API Response received");
                },
            }).catch((error) => {
                fetchBookSuccess = false;
                previewRequestError = error;
                previewRequestDiagnostics = getPreviewRequestDiagnostics(error, {
                    requestMode,
                    requestUrl: props.requestUrl,
                });
                const safeDiagnostics = getPreviewFailureMetricAttributes(
                    previewRequestDiagnostics
                );

                if (isPollCancelledError(error)) {
                    return;
                }

                if (isDevMode) {
                    console.error("Error fetching book", {
                        code: getAxiosCode(error),
                        status: getAxiosHttpStatus(error) || "none",
                        pollPhase: previewRequestDiagnostics.pollPhase || "none",
                    });
                }

                captureCustomizerException(error, {
                    tags: {
                        component: "BookPreview",
                        action: "loadBook",
                        requestMode,
                        request_mode: requestMode,
                        product_type: "book",
                        variant: requestBody.variantID,
                        host: window.location.host,
                        job_id: safeDiagnostics.job_id,
                        response_status: safeDiagnostics.response_status,
                        failed_host: safeDiagnostics.failed_host,
                        poll_phase: previewRequestDiagnostics.pollPhase || "none",
                        http_status: safeDiagnostics.response_status,
                        axios_code: previewRequestDiagnostics.axiosCode || "none",
                        elapsed_ms: previewRequestDiagnostics.elapsedMs ?? "unknown",
                    },
                    context: {
                        elapsedMs: Date.now() - requestStartedAt,
                        failed_host: safeDiagnostics.failed_host,
                        finalStatusCheck: Boolean(previewRequestDiagnostics.finalCheck),
                    },
                });
            });

            Promise.resolve(fetchBookPromise)
                .then(() => {
                    if (!fetchBookSuccess) {
                        if (!isPollCancelledError(previewRequestError)) {
                            showLoadError(
                                customizerText("previewLoadError"),
                                getPreviewFailureReason(previewRequestError, "request_failed"),
                                previewRequestDiagnostics
                            );
                        }
                        return;
                    }

                    finishBookLoading();
                })
                .catch(error => {
                    captureCustomizerException(error, {
                        tags: { component: "BookPreview", failure_reason: "draft_load_promise_failed" },
                    });
                    if (allowRedirect) {
                        redirectToStep1('Draft loading promise failed', pageSettings);
                    } else {
                        showLoadError(
                            customizerText("draftLoadError"),
                            "unexpected_load_failure"
                        );
                    }
                });
        };

        function loadBook() {
            const draftId = getDraftIdFromUrlCached();
            const bookId = route.params.bookId || null;
            
            if (draftId) {
                storeDraftMeta(bookId, draftId);
                
                DraftService.loadDraftData(draftId).then(result => {
                    if (result.success && isDraftDataValid(result.data)) {
                        const loadedDraftData = result.data;
                        draftData.value = loadedDraftData;
                        restorePreviewDraftValues(loadedDraftData, pageSettings);
                        
                        book.value = buildBookPayload(loadedDraftData.pages, loadedDraftData);
                        EventBus.emit("bookLoaded", book.value);
                        reportPreviewOutcome("success");
                        
                        bookLoading.value.style.opacity = 0;
                        
                        if (props.fadeInClass !== "") {
                            const contentBoxes = document.getElementsByClassName(props.fadeInClass);
                            for (let i = 0; i < contentBoxes.length; i++) {
                                contentBoxes[i].style.display = "flex";
                                contentBoxes[i].style.transition = "opacity 0.3s";
                                contentBoxes[i].style.opacity = 1;
                            }
                        }
                        
                        if (props.hideAfterLoadingClass !== "") {
                            const elementsToHide = document.getElementsByClassName(props.hideAfterLoadingClass);
                            for (let i = 0; i < elementsToHide.length; i++) {
                                elementsToHide[i].style.transition = "opacity 0.3s";
                                elementsToHide[i].style.opacity = 0;
                                setTimeout(() => {
                                    elementsToHide[i].style.display = "none";
                                }, 300);
                            }
                        }
                        
                        setTimeout(() => {
                            loading.value = false;
                            EventBus.emit("stepThreeShowContent");
                            showConfetti();
                        }, 300);
                        
                        return;
                    } else {
                        handleDraftLoadFailure(
                            'Draft loading failed or invalid',
                            true,
                            result.data,
                            result.diagnostics
                        );
                    }
                }).catch(error => {
                    captureCustomizerException(error, {
                        tags: { component: "BookPreview", failure_reason: "draft_load_promise_failed" },
                    });
                    handleDraftLoadFailure('Draft loading promise failed', true);
                });
            } else {
                clearDraftMeta();
                loadBookNormal();
            }
        }

        function loadBookNormal(options = {}) {
            const allowRedirect = options.allowRedirect !== false;
            const step1Values = safeGetJson(pageSettings.step1StorageKey, {});
            const step2Values = safeGetJson(pageSettings.step2StorageKey, {});

            const variantId = pageSettings.variant || "";

            if (
                Object.keys(step1Values).length === 0 ||
                Object.keys(step2Values).length === 0
            ) {
                if (shouldStayOnCurrentStepForPreview()) {
                    showLoadError(
                        "Für diese Preview fehlen Step-1- oder Step-2-Daten.",
                        "missing_customizer_data"
                    );
                    return;
                }

                if (allowRedirect) {
                    redirectToStep1('Step 1 or Step 2 values are empty', pageSettings);
                } else {
                    showLoadError(
                        customizerText("draftLoadError"),
                        "missing_customizer_data"
                    );
                }
                return;
            }

            const requestBody = {
                step1Values: step1Values,
                step2Values: step2Values,
                variantID: variantId,
                minLoadingDuration: props.minLoadingDuration,
                currentUrl: window.location.href
            };

            makeBookRequest(requestBody, {
                allowRedirect,
                forceNewPollJob: options.forceNewPollJob,
            });
        }

        function changeRequested() {
            changing.value = true;
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }

        function changeRequestResponse(response) {
            changing.value = false;
            const payload = getDraftPayloadFromResponse(response);

            if (!payload?.pages) {
                return;
            }

            const transformedPages = Object.entries(payload.pages).reduce(
                (acc, [pageKey, pageData]) => {
                    acc[pageKey] = {
                        img: pageData.img,
                        text: pageData.text,
                        imgprompt: pageData.imgprompt,
                    };
                    return acc;
                },
                {}
            );

            book.value = buildBookPayload(transformedPages, payload);
            EventBus.emit("bookLoaded", book.value);
            
            const responseDraftId = payload.draftid || null;
            if (responseDraftId) {
                storeDraftMeta(route.params.bookId || null, responseDraftId);
                
            }
        }

        const activatePreview = () => {
            if (!componentActive || previewActivated) {
                return;
            }

            previewActivated = true;
            window.addEventListener("resize", handleResize);
            EventBus.on("changeRequestSubmitted", changeRequested);
            EventBus.on("changeRequestResponse", changeRequestResponse);
            loadBook();
        };

        onMounted(() => {
            nextTick(() => {
                if (!componentActive) return;

                stopBuilderVariantActivity = startWhenBuilderVariantActive(
                    previewRoot.value,
                    activatePreview
                );
            });
        });

        onBeforeUnmount(() => {
            componentActive = false;
            stopBuilderVariantActivity?.();
            stopBuilderVariantActivity = null;
            clearPollTimeoutHandle();
            window.removeEventListener("resize", handleResize);
            EventBus.off("changeRequestSubmitted", changeRequested);
            EventBus.off("changeRequestResponse", changeRequestResponse);
        });

        watch(book, (newValue) => {
            EventBus.emit("bookChanged", newValue);
        });

        function onPreviewSwiper(_swiper) {
            previewSwiper.value = _swiper;
        }

        function onPreviewSlideChange() {
            if (!previewSwiper.value) {
                return;
            }

            previewSliderActiveIndex.value = previewSwiper.value.realIndex;
            EventBus.emit(
                "previewSlideChanged",
                previewSliderActiveIndex.value + 1
            );
            
            trackPreviewInteraction();

            if (props.previewMode === 'walnut') {
                nextTick(() => {
                    const containerRef =
                        pageTextContainer.value?.[previewSliderActiveIndex.value - 1];
                    const textRef =
                        pageTextParagraph.value?.[previewSliderActiveIndex.value - 1];
                    adjustTextSize(containerRef, textRef);
                });
            }
        }

        const processedTitleContent = computed(() => {
            if (book.value?.pages?.page1?.text) {
                return book.value.pages.page1.text;
            }
            
            return resolveTextTokens(props.titleContent, ({ storageKey, pathParts }) => {
                let value = null;
                let draftKey = null;

                if (storageKey.startsWith("step1Values")) {
                    draftKey = "step1Values";
                } else if (storageKey.startsWith("step2Values")) {
                    draftKey = "step2Values";
                } else if (storageKey.startsWith("step3Values")) {
                    draftKey = "step3Values";
                }

                if (draftKey && draftData.value?.[draftKey]) {
                    value = getTokenValue(draftData.value[draftKey], pathParts);
                }

                if (value === null || value === undefined || value === "") {
                    value = getTokenValue(safeGetJson(storageKey, null), pathParts);
                }

                return value;
            });
        });

        const showTitlePrefix = computed(() => {
            return !(book.value?.pages?.page1?.text);
        });

        const adjustTextSize = (containerRef, textRef) => {
            if (!containerRef || !textRef) return;

            let maxHeight = containerRef.clientHeight;
            let maxWidth = containerRef.clientWidth;
            let fontSize = 16;

            textRef.style.fontSize = `${fontSize}px`;

            while (
                textRef.scrollHeight > maxHeight ||
                textRef.scrollWidth > maxWidth
            ) {
                fontSize--;
                textRef.style.fontSize = `${fontSize}px`;

                if (fontSize <= 10) {
                    break;
                }
            }
        };

        const debounce = (func, delay) => {
            let timeoutId;
            return (...args) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                timeoutId = setTimeout(() => {
                    func(...args);
                }, delay);
            };
        };

        const handleResize = debounce(() => {
            if (previewSliderActiveIndex.value <= 0) {
                return;
            }

            if (props.previewMode === 'walnut') {
                const containerRef =
                    pageTextContainer.value?.[previewSliderActiveIndex.value - 1];
                const textRef =
                    pageTextParagraph.value?.[previewSliderActiveIndex.value - 1];
                adjustTextSize(containerRef, textRef);
            }
        }, 100);

        const loadingStuckLabel = computed(() => builderText(props.loadingStuckText, "bookLoadingStuck"));

        return {
            loadingStuckLabel,
            onPreviewImageError,
            loading,
            previewRoot,
            bookLoading,
            loadingPercentage,
            loadingSwiper,
            loadingSliderActiveIndex,
            loadingSliderSlidesPerView,
            onLoadingSwiper,
            Autoplay,
            onLoadingSlideChange,
            getLoadingSlideStyle,
            previewSwiper,
            previewSliderActiveIndex,
            onPreviewSwiper,
            onPreviewSlideChange,
            processedTitleContent,
            showTitlePrefix,
            pageTextContainer,
            pageTextParagraph,
            book,
            changing,
            handlePrevClick,
            handleNextClick,
            handlePaginationClick,
            showLoadingStuckText,
            draftData,
            softcoverSlides,
            loadError
        };
    },
};
</script>


<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;
.book-preview {
    position: relative;

    .book-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        row-gap: 24px;
        width: 100%;
        transition: opacity 0.3s;

        @media (max-width: $breakpoint-mobile) {
            row-gap: 16px;
        }

        .loading-bar-wrapper {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            row-gap: 12px;
            width: 100%;
            max-width: 500px;

            @media (max-width: $breakpoint-mobile) {
                order: 1;
                max-width: unset;
                row-gap: 16px;
            }

            .loading-number-wrapper {
                width: 100%;
                text-align: center;

                @media (max-width: $breakpoint-mobile) {
                    display: none;
                }

                .loading-number {
                    color: $color-new-black;
                    display: inline;
                    font-weight: 600;
                    font-size: 20px;
                }
            }

            .loading-bar {
                width: 100%;
                height: 18px;
                position: relative;

                @media (max-width: $breakpoint-mobile) {
                    height: 24px;
                }

                .loading-bar-track {
                    width: 100%;
                    height: 100%;
                    background: $color-light-gray;
                    border-radius: 18px;
                    overflow: hidden;
                    position: relative;
                    box-shadow:
                        inset 0 2px 6px rgba(0, 0, 0, 0.15),
                        0 4px 12px rgba(126, 187, 204, 0.25);

                    @media (max-width: $breakpoint-mobile) {
                        border-radius: 24px;
                    }

                    .loading-bar-fill {
                        height: 100%;
                        background: linear-gradient(
                            90deg,
                            $color-primary-dt-blue 0%,
                            rgba($color-primary-dt-blue, 0.3) 20%,
                            transparent 40%,
                            rgba($color-secondary-dt-orange, 0.3) 60%,
                            $color-secondary-dt-orange 80%,
                            rgba($color-secondary-dt-orange, 0.3) 90%,
                            transparent 100%
                        );
                        background-size: 300% 100%;
                        border-radius: 18px;
                        position: relative;
                        overflow: hidden;
                        transition: width 0.15s ease-out;
                        box-shadow: 0 4px 12px rgba(126, 187, 204, 0.5);
                        animation: gradientShift 12s ease-in-out infinite;


                        @media (max-width: $breakpoint-mobile) {
                            border-radius: 24px;
                        }


                        .loading-number-mobile {
                            display: none;
                            position: absolute;
                            right: 8px;
                            top: 50%;
                            transform: translateY(-50%);
                            font-size: 14px;
                            font-weight: 700;
                            color: $color-new-black;
                            text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
                            z-index: 2;

                            @media (max-width: $breakpoint-mobile) {
                                display: inline-block;
                                font-size: 16px;
                                right: 12px;
                            }
                        }
                    }

                    .loading-bar-glow {
                        position: absolute;
                        top: -3px;
                        left: 0;
                        height: calc(100% + 6px);
                        background: linear-gradient(
                            90deg,
                            $color-dt-light-blue-1 0%,
                            $color-dt-light-blue-2 25%,
                            $color-primary-dt-blue 50%,
                            $color-dt-dark-blue-1 75%,
                            $color-dt-dark-blue-2 100%
                        );
                        border-radius: 21px;
                        opacity: 0.4;
                        filter: blur(6px);
                        transition: width 0.15s ease-out;
                        z-index: -1;

                        @media (max-width: $breakpoint-mobile) {
                            border-radius: 27px;
                            filter: blur(8px);
                        }
                    }
                }
            }
        }

        @keyframes gradientShift {
            0% {
                background-position: 0% 50%;
            }
            50% {
                background-position: 100% 50%;
            }
            100% {
                background-position: 0% 50%;
            }
        }


        .loading-stuck-text {
            margin: 0;
            font-family: "TT Commons Pro", sans-serif;
            font-size: 16px;
            color: $color-new-black;
            text-align: center;
        }

        .loading-book-img {
            width: 100%;
            max-width: 291px;

            @media (max-width: $breakpoint-mobile) {
                order: 3;
                max-width: 209px;
            }
        }

        .swiper {
            width: calc(100% + 80px);
            margin: 0 -40px;

            @media (max-width: $breakpoint-mobile) {
                order: 2;
                width: 100%;
                margin: 0;
            }

            .swiper-slide {
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 24px 32px;
                border-radius: 20px;
                background-color: $color-white;
                transition: opacity 0.3s ease;

                @media (max-width: $breakpoint-mobile) {
                    opacity: 1 !important;
                    transition: none;
                }
            }
        }
    }

    .book-content {
        &.is-changing {
            .first-page,
            .page-n,
            .softcover-title-page,
            .softcover-image-page,
            .softcover-text-page {
                filter: blur(8px);
                transition: filter 0.5s ease-in;
            }
        }

        .swiper-wrapper {
            position: relative;
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            column-gap: 16px;
            width: 100%;

            .first-page,
            .page-n {
                display: flex;
                flex-direction: row;
                justify-content: center;
                width: 100%;
                transition: filter 0.5s ease-out;

                .page-images {
                    position: relative;
                    overflow: hidden;

                    .page-image {
                        position: absolute;
                        z-index: 1;
                        left: 50%;
                        width: 100%;
                        height: auto;
                        object-fit: contain;
                        transform: translateX(-50%);
                    }

                    .page-overlay {
                        position: relative;
                        z-index: 2;
                        width: 100%;
                        max-width: 400px;
                        height: auto;
                        object-fit: contain;
                        border-radius: 20px;
                        border: 1px solid $color-normal-grey;
                        overflow: hidden;
                    }
                }
            }

            .first-page {
                .page-title {
                    position: absolute;
                    top: 15%;
                    left: 50%;
                    z-index: 3;
                    transform: translateX(-50%);
                    font-family: "TT Commons Pro", sans-serif;
                    font-size: 24px;
                    font-weight: 100;
                    letter-spacing: 0.6px;
                    line-height: 37px;
                    text-align: center;
                    width: 90%;
                    max-width: 350px;

                    @media (max-width: $breakpoint-mobile) {
                        top: 13%;
                        font-size: 20px;
                        line-height: 24px;
                        width: 85%;
                        max-width: 280px;
                    }

                    .title-prefix {
                        color: $color-new-black;
                        max-width: 350px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .title {
                        padding-top: 10px;
                        display: inline-block;
                        font-family: "Vegan Style", serif;
                        max-width: 600px;
                        margin: 10px 0 0;
                        white-space: normal;
                        word-wrap: break-word;
                        font-size: 32px;
                        color: $color-new-black;

                        @media (max-width: $breakpoint-mobile) {
                            max-width: 280px;
                            font-size: 28px;
                            line-height: 1.1;
                        }

                        // Vegan Style zeichnet „ł“ wie „t“ („Michała“ → „Michata“).
                        &:lang(pl) {
                            font-family: "Playball", serif;
                        }
                    }
                }

                .page-images {
                    .page-image {
                        bottom: 1.5%;
                        max-width: 80%;
                    }
                }
            }

                .page-n {
                .page-images {
                    .page-image {
                        top: 1%;
                        left: 50%;
                        max-width: 370px;
                        max-height: 65%;
                        border-radius: 30px;
                        transform: translateX(-50%);
                        overflow: hidden;
                        object-fit: cover;
                    }
                }

                .page-text {
                    position: absolute;
                    bottom: 8%;
                    z-index: 3;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    width: 80%;
                    max-width: 290px;
                    height: 30%;
                    max-height: 190px;

                    p {
                        margin: 0;
                        font-family: "Handlee", sans-serif;
                        font-size: 16px;
                        text-align: center;
                    }
                }
            }

            .custom-swiper-prev,
            .custom-swiper-next {
                position: unset;
                display: flex;
                justify-content: center;
                align-items: center;
                min-width: 40px;
                min-height: 40px;
                width: 40px;
                height: 40px;
                padding: 5px;
                background-color: $color-secondary-dt-orange;
                border-radius: 50%;
                border: none;
                z-index: 10;
                overflow: hidden;
                cursor: pointer;

                @media (max-width: $breakpoint-mobile) {
                    position: absolute;
                    width: 34px;
                    height: 34px;
                }

                &::before {
                    content: "";
                    display: block;
                    width: 100%;
                    height: 100%;
                    background-image: url("~@/assets/customizer_icon_slider_btn_left.svg");
                    background-size: contain;
                    background-position: center;
                    background-repeat: no-repeat;
                    background-clip: content-box;
                }

                &.disabled {
                    cursor: not-allowed;
                    opacity: 0.5;
                }
            }

            .custom-swiper-prev {
                @media (max-width: $breakpoint-mobile) {
                    left: -8px;
                }
            }

            .custom-swiper-next {
                transform: rotate(180deg);

                @media (max-width: $breakpoint-mobile) {
                    right: -8px;
                }
            }
        }

        .swiper-pagination {
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            column-gap: 5px;
            width: 100%;
            margin-top: 16px;

            span {
                display: block;
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background-color: $color-light-gray;
                cursor: pointer;

                &:hover,
                &.active {
                    background-color: $color-new-black;
                }
            }
        }
    }

    .softcover-mode {
        .swiper-wrapper {
            .softcover-title-page,
            .softcover-image-page,
            .softcover-text-page {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                width: 100%;
                max-width: 500px;
                aspect-ratio: 1;
                margin: 0 auto;
                transition: filter 0.5s ease-out;

                @media (max-width: $breakpoint-mobile) {
                    max-width: 350px;
                }
            }

            .softcover-title-page {
                position: relative;

                .page-title {
                    position: absolute;
                    top: 20%;
                    left: 50%;
                    z-index: 3;
                    transform: translateX(-50%);
                    font-family: "TT Commons Pro", sans-serif;
                    font-size: 20px;
                    font-weight: 100;
                    letter-spacing: 0.6px;
                    line-height: 30px;
                    text-align: center;
                    width: 90%;
                    max-width: 300px;

                    @media (max-width: $breakpoint-mobile) {
                        top: 18%;
                        font-size: 18px;
                        line-height: 24px;
                        width: 85%;
                        max-width: 250px;
                    }

                    .title-prefix {
                        color: $color-new-black;
                        max-width: 300px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .title {
                        padding-top: 8px;
                        display: inline-block;
                        font-family: "Vegan Style", serif;
                        max-width: 400px;
                        margin: 8px 0 0;
                        white-space: normal;
                        word-wrap: break-word;
                        font-size: 28px;
                        color: $color-new-black;

                        @media (max-width: $breakpoint-mobile) {
                            max-width: 250px;
                            font-size: 24px;
                            line-height: 1.1;
                        }

                        // Vegan Style zeichnet „ł“ wie „t“ („Michała“ → „Michata“).
                        &:lang(pl) {
                            font-family: "Playball", serif;
                        }
                    }
                }

                .softcover-title-images {
                    position: relative;
                    overflow: hidden;

                    .softcover-title-background {
                        position: absolute;
                        z-index: 1;
                        left: 50%;
                        top: 35%;
                        width: 63%;
                        height: auto;
                        object-fit: contain;
                        transform: translateX(-50%);
                    }

                    .softcover-title-overlay {
                        position: relative;
                        z-index: 2;
                        width: 100%;
                        height: auto;
                        object-fit: contain;
                        border-radius: 20px;
                        border: 1px solid $color-normal-grey;
                        overflow: hidden;
                    }
                }
            }

            .softcover-image-page {
                .softcover-page-image {
                    width: 100%;
                    height: auto;
                    object-fit: contain;
                    border-radius: 20px;
                    border: 1px solid $color-normal-grey;
                    overflow: hidden;
                }
            }

            .softcover-text-page {
                position: relative;

                .softcover-paper-texture {
                    width: 100%;
                    height: auto;
                    object-fit: contain;
                    border-radius: 20px;
                    border: 1px solid $color-normal-grey;
                    overflow: hidden;
                }

                .softcover-text-content {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    z-index: 3;
                    transform: translate(-50%, -50%);
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    width: 80%;
                    max-width: 300px;
                    height: 60%;
                    max-height: 200px;

                    @media (max-width: $breakpoint-mobile) {
                        width: 85%;
                        max-width: 250px;
                        height: 65%;
                        max-height: 180px;
                    }

                    p {
                        margin: 0;
                        font-family: "Handlee", sans-serif;
                        font-size: 16px;
                        text-align: center;
                        color: $color-new-black;
                        line-height: 1.4;

                        @media (max-width: $breakpoint-mobile) {
                            font-size: 14px;
                        }
                    }
                }
            }
        }
    }

    .book-error {
        margin: 24px auto;
        padding: 16px;
        max-width: 560px;
        text-align: center;
        background: #f9e8e8;
        border: 1px solid #e2b3b3;
        border-radius: 12px;
        color: #8a2f2f;
        font-weight: 600;
        line-height: 1.4;
    }

    .change-indicator {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 80px;
        height: 80px;
        transform: translate(-50%, -50%);
        z-index: 10;
        opacity: 0;
        animation: fadeIn 0.5s ease-in forwards;
        display: flex;
        align-items: center;
        justify-content: center;

        
        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
        
        @keyframes spin {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }
    }
}
</style>
